// ============================================================================
// CLEO'S SECRET — Browser-side Supabase client
// File: src/lib/supabase-browser.js
//
// Browser-side singleton client + auth helpers.
//   • Magic link (signInWithMagicLink) — patient flow
//   • Password    (signInWithPassword) — admin/staff flow
// Uses the public anon key, safe to expose in the browser bundle.
// Both env vars are set in Vercel: REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_ANON_KEY
// ============================================================================

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

let cached = null;

export function getBrowserClient() {
  if (cached) return cached;

  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.warn(
      'Supabase browser client not configured. ' +
      'Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to Vercel.'
    );
    return null;
  }

  cached = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });

  return cached;
}

export async function getCurrentUser() {
  const supabase = getBrowserClient();
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

// ───────── Patient flow: magic link (custom endpoint, NOT Supabase Auth's email) ─────────
// POSTs to our /api/auth/magic-link-send endpoint. The endpoint ALWAYS
// returns { ok: true } regardless of whether the email is registered,
// rate-limited, malformed, or whether sending succeeded — silent
// enumeration protection. We mirror that contract here: resolve to
// { success: true } on any 2xx response so the caller's UI always shows
// the "check your email" screen.
//
// Network/transport failures (offline, DNS, CORS) throw — caller catches
// to show a recoverable error. Same on 5xx where the server itself is
// down: "service unavailable" is the only failure the user-facing UI
// distinguishes.
export async function signInWithMagicLink(email, returnTo) {
  const cleanEmail = email.toLowerCase().trim();
  const body = { email: cleanEmail };
  // returnTo is an optional patient-destination route (e.g. 'program').
  // The server validates against VALID_RETURN_TO_PAGES before embedding
  // in the verify URL; invalid values are silently dropped server-side.
  if (returnTo && typeof returnTo === 'string') body.returnTo = returnTo;

  let response;
  try {
    response = await fetch('/api/auth/magic-link-send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new Error('Could not reach sign-in service. Please check your connection and try again.');
  }

  if (!response.ok) {
    // 2xx is the only success contract from the send endpoint. Anything
    // else (5xx, or an impossible 4xx if our request shape is correct) is
    // a server-side failure we surface to the user.
    throw new Error('Sign-in service is unavailable. Please try again in a moment.');
  }

  return { success: true };
}

// ───────── Patient flow: verify magic link token + mint session ─────────
// Consumed by AuthVerify.jsx (commit 6) after the patient clicks the
// "Sign in" button on the /auth/verify landing page (the page does NOT
// auto-consume on mount — see session-5-backlog for the scanner-pre-fetch
// rationale).
//
// On success: calls supabase.auth.setSession() with the access/refresh
// tokens from the verify endpoint. This triggers Supabase's SIGNED_IN
// event which App.jsx's existing onAuthStateChange handler picks up to
// route to portal/admin.
//
// On 401 (invalid / expired / consumed token): throws with a specific
// message so the caller can render the "link expired" branded copy.
// All other failures (network, 5xx, malformed response) get distinct
// messages so support requests can distinguish causes.
export async function verifyMagicLinkToken(token) {
  const supabase = getBrowserClient();
  if (!supabase) throw new Error('Supabase not configured');

  let response;
  try {
    response = await fetch('/api/auth/magic-link-verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch (err) {
    throw new Error('Could not reach sign-in service. Please check your connection and try again.');
  }

  if (response.status === 401) {
    throw new Error('This link is invalid or has expired.');
  }
  if (!response.ok) {
    throw new Error('Sign-in service is unavailable. Please try again in a moment.');
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    throw new Error('Sign-in response malformed. Please try again.');
  }
  if (!data || !data.access_token || !data.refresh_token) {
    throw new Error('Sign-in response malformed. Please try again.');
  }

  // Note: if the user already has an active session (e.g., signed in on
  // another tab and clicked the link in this tab), setSession replaces the
  // existing session with the new one. Same user, fresh expiry — harmless.
  // Old/consumed magic links 401 before reaching this point, so the
  // existing session is untouched on failure.
  const { error: sessionErr } = await supabase.auth.setSession({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
  });
  if (sessionErr) {
    throw new Error('Could not establish session. Please try again.');
  }

  return {
    user_id: data.user_id,
    first_name: data.first_name || null,
  };
}

// ───────── Admin/staff flow: email + password ─────────
// Used by AdminPanel sign-in gate. Admins are seeded manually in Supabase
// Auth dashboard with passwords — no public sign-up flow.
export async function signInWithPassword(email, password) {
  const supabase = getBrowserClient();
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.toLowerCase().trim(),
    password: password,
  });

  if (error) throw error;
  return { success: true, user: data.user };
}

export async function signOut() {
  const supabase = getBrowserClient();
  if (!supabase) return;
  await supabase.auth.signOut();
}
